export interface ValidationResult {
  isValid: boolean
  errors: Record<string, string>
}

export interface FormData {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  birthday: string
  zip: string
}

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long')
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter')
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter')
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number')
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

export const validateZipCode = (zip: string): boolean => {
  const zipRegex = /^\d{5}(-\d{4})?$/
  return zipRegex.test(zip)
}

export const validateAge = (birthday: string): { isValid: boolean; age?: number; error?: string } => {
  if (!birthday) {
    return { isValid: false, error: 'Birthday is required' }
  }
  
  const birthDate = new Date(birthday)
  const today = new Date()
  const age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  
  const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
    ? age - 1 
    : age
  
  if (actualAge < 18) {
    return { isValid: false, age: actualAge, error: 'You must be at least 18 years old to use DateShake' }
  }
  
  if (actualAge > 125) {
    return { isValid: false, age: actualAge, error: 'Please enter a valid birth date' }
  }
  
  return { isValid: true, age: actualAge }
}

export const validateRegistrationForm = (data: FormData): ValidationResult => {
  const errors: Record<string, string> = {}
  
  // First Name validation
  if (!data.firstName.trim()) {
    errors.firstName = 'First name is required'
  } else if (data.firstName.trim().length < 2) {
    errors.firstName = 'First name must be at least 2 characters'
  }
  
  // Last Name validation
  if (!data.lastName.trim()) {
    errors.lastName = 'Last name is required'
  } else if (data.lastName.trim().length < 2) {
    errors.lastName = 'Last name must be at least 2 characters'
  }
  
  // Email validation
  if (!data.email.trim()) {
    errors.email = 'Email is required'
  } else if (!validateEmail(data.email)) {
    errors.email = 'Please enter a valid email address'
  }
  
  // Password validation
  if (!data.password) {
    errors.password = 'Password is required'
  } else {
    const passwordValidation = validatePassword(data.password)
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.errors[0] // Show first error
    }
  }
  
  // Confirm Password validation
  if (!data.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password'
  } else if (data.password !== data.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match'
  }
  
  // Birthday validation
  const ageValidation = validateAge(data.birthday)
  if (!ageValidation.isValid) {
    errors.birthday = ageValidation.error || 'Please enter a valid birth date'
  }
  
  // Zip code validation
  if (!data.zip.trim()) {
    errors.zip = 'Zip code is required'
  } else if (!validateZipCode(data.zip)) {
    errors.zip = 'Please enter a valid 5-digit zip code'
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}

export const getPasswordStrength = (password: string): { score: number; label: string; color: string } => {
  let score = 0
  
  if (password.length >= 8) score++
  if (/[A-Z]/.test(password)) score++
  if (/[a-z]/.test(password)) score++
  if (/\d/.test(password)) score++
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++
  
  if (score <= 2) {
    return { score, label: 'Weak', color: 'text-red-600' }
  } else if (score <= 3) {
    return { score, label: 'Fair', color: 'text-yellow-600' }
  } else if (score <= 4) {
    return { score, label: 'Good', color: 'text-blue-600' }
  } else {
    return { score, label: 'Strong', color: 'text-green-600' }
  }
}
